import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { changeTicketDetailsApi, createTicketMessageApi } from 'api/tickets';
import { useTickets } from 'contexts/TicketsContext';
import type { GetTicketsResponseDTO } from 'declarations/dto/tickets';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { RoutePathsE } from 'declarations/enums/routes';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import {
  AssigneeFilterValuesE,
  type ScheduleWorkTypeE,
  TicketAssigneeE,
  isScheduleWorkType,
} from 'declarations/enums/tickets';
import type { NoopT } from 'declarations/types/common';
import { getLastMessageDisplayText } from 'pages/TicketsPage/helpers';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import useChangeTicketDetailsForm from '../components/TicketDetailsPopup/hooks/useChangeTicketDetailsForm';
export interface Column {
  id: string;
  label: string;
  visible: boolean;
}
export interface TicketsPageHookProps {
  ticketsPages: GetTicketsResponseDTO[] | undefined | null;
}

/**
 * Map assignee display value to backend TicketUserTypes enum.
 * Backend enum: Undefined=0, Chirp=1 (Dealer), WarrCloud=2.
 * OEM is not a valid backend TicketUserTypes value.
 * When the assignee dropdown shows dealer options, the value can be a rooftop name or "Chirp".
 */
const toAssignToEnum = (assigneeName: string | undefined): string | null => {
  if (!assigneeName || assigneeName.trim() === '') return null;

  const normalizedAssignee = assigneeName.trim().toLowerCase();
  if (
    normalizedAssignee === AssigneeFilterValuesE.WarrCloud.toLowerCase() ||
    normalizedAssignee === TicketAssigneeE.WarrCloud.toLowerCase()
  ) {
    return TicketAssigneeE.WarrCloud;
  }

  // OEM cannot be assigned through ticket details update endpoint.
  if (normalizedAssignee === AssigneeFilterValuesE.OEM.toLowerCase()) {
    return null;
  }

  // Dealer, Chirp, or rooftop name (dealer) -> Chirp
  return TicketAssigneeE.Chirp;
};

/** Claim status IDs that require OEM assignee. 1=Pending, 7=ManualReview. */
const OEM_RESERVED_CLAIM_STATUS_IDS = [1, 7];

const isClaimStatusOemReserved = (claimStatusId: number): boolean =>
  OEM_RESERVED_CLAIM_STATUS_IDS.includes(Number(claimStatusId));

const isAssigneeOem = (assigneeName: string | undefined): boolean =>
  (assigneeName ?? '').trim().toUpperCase() === 'OEM';

export interface ChangeTicketsListFormValues {
  ticketId: string;
  ticketRoofTopId: string;
  amount: string;
  claimStatusId: number;
  claimSubmittedDate: Date | null;
  advisorNumber: string;
  techNumber: string;
  ticketPriorityId: number;
  subCategoryId: number | null;
  workTypeId: ScheduleWorkTypeE | null;
  text: string;
  dmsScheduleAccountNumber: string | null;
  assigneeName: string;
}

export type SelectTicketPayloadT = {
  roNumber: string;
  lineNumber: string | null;
};

type UseTicketsPageT = {
  formMethods: UseFormReturn<Record<string, ChangeTicketsListFormValues>>;
  handleSaveChanges: (data: Record<string, ChangeTicketsListFormValues>) => Promise<void>;
  hasNextTicketsPage: boolean | undefined;
  handleSelectTicket: (ticketId: string, payload: SelectTicketPayloadT) => NoopT;
};

const useTicketsPage = ({ ticketsPages }: TicketsPageHookProps): UseTicketsPageT => {
  /**
   * Hooks
   */
  const dispatch = useAppDispatch();
  const {
    setTicketId,
    setEditMode,
    setIsSavingTickets,
    setOpenedTicketTab,
    setOpenedScheduleTicketTab,
    setActiveTab,
    saveTicketListScrollPosition,
  } = useTickets();
  const { activeTab } = useTickets();
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Handlers
   */

  const queryClient = useQueryClient();
  const formMethods = useForm<Record<string, ChangeTicketsListFormValues>>({ mode: 'onChange' });
  const { setSelectedTicketsIds, hasNextTicketsPage, selectedTicketsIds } = useTickets();
  const { ticketSubcategoriesOptions, ticketPrioritiesOptions } = useChangeTicketDetailsForm();

  const normalizeString = (str: string): string => str?.replace(/\s+/g, '').toLowerCase();

  const handleSaveChanges = useCallback(
    async (data: Record<string, ChangeTicketsListFormValues>) => {
      setIsSavingTickets(true);

      const mutationPromises: Promise<void>[] = [];
      let ticketsMutatedCount = 0;
      const findOptionIdByName = (
        options: { label: string; value: string | number }[],
        name: string | null
      ): string | number | null => {
        const normalizedTarget = name ? normalizeString(name) : null;
        const option = options.find(option => normalizeString(option.label) === normalizedTarget);
        return option ? option.value : null;
      };

      selectedTicketsIds.forEach(ticketId => {
        const ticketData = data[`[${ticketId}]`] ?? data[ticketId];
        const currentTicket = ticketsPages
          ?.flatMap(page => page.data)
          .find(ticket => ticket.id === ticketId);

        if (!currentTicket) {
          return;
        }

        const claimStatusId = ticketData?.claimStatusId ?? currentTicket.claimStatusId;
        const assigneeName = ticketData?.assigneeName ?? currentTicket.assigneeName;
        const currentAssignTo = toAssignToEnum(currentTicket.assigneeName ?? undefined);
        const nextAssignTo = toAssignToEnum(assigneeName);

        if (isClaimStatusOemReserved(claimStatusId) && !isAssigneeOem(assigneeName)) {
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: `Ticket ${currentTicket.roNumber} cannot be assigned to ${
                assigneeName ?? 'this assignee'
              } while claim status is ${
                currentTicket.claimStatus ?? 'Pending'
              }. Selecting the correct claim status will automatically assign this ticket to the OEM.`,
              snackBarSeverity: 'warning',
            })
          );
          return;
        }

        // Determine if this is a schedule ticket
        const isScheduleTicket = currentTicket.categoryName?.toLowerCase() === 'schedule';

        // Get subcategory ID - for schedule tickets use subCategoryId, for regular tickets use requestSubCategoryId
        const subCategoryId =
          ticketData?.subCategoryId ??
          (isScheduleTicket ? currentTicket.subCategoryId : currentTicket.requestSubCategoryId) ??
          findOptionIdByName(ticketSubcategoriesOptions, currentTicket.subCategory);

        const ticketPriorityId =
          ticketData?.ticketPriorityId ??
          ticketPrioritiesOptions.find(option => option.label === currentTicket.ticketPriority)
            ?.value;
        const normalizedAmount = ticketData?.amount
          ? ticketData.amount.replace(/[^0-9.-]/g, '')
          : currentTicket.amount.toString();
        const advisorNumber = ticketData?.advisorNumber || currentTicket.advisorNumber;
        const techNumber =
          ticketData?.techNumber === '-' ? '' : ticketData?.techNumber || currentTicket.techNumber;
        const dmsScheduleAccountNumber =
          ticketData?.dmsScheduleAccountNumber ?? currentTicket.dmsScheduleAccountNumber;
        const normalizedSubCategoryId = subCategoryId ? Number(subCategoryId) : null;
        const nextScheduleSubCategoryId = isScheduleTicket ? normalizedSubCategoryId : null;
        const nextRequestSubCategoryId = !isScheduleTicket ? normalizedSubCategoryId : null;
        const currentScheduleSubCategoryId = currentTicket.subCategoryId ?? null;
        const currentRequestSubCategoryId = currentTicket.requestSubCategoryId ?? null;
        const currentWorkTypeValue = currentTicket.workTypeId ?? currentTicket.workType;
        const currentWorkTypeId = isScheduleWorkType(currentWorkTypeValue)
          ? currentWorkTypeValue
          : null;
        const nextWorkTypeId = isScheduleTicket
          ? ticketData?.workTypeId ??
            (isScheduleWorkType(currentWorkTypeValue) ? currentWorkTypeValue : null)
          : null;
        const hasDetailsChanges =
          normalizedAmount !== currentTicket.amount.toString() ||
          claimStatusId !== currentTicket.claimStatusId ||
          advisorNumber !== currentTicket.advisorNumber ||
          techNumber !== currentTicket.techNumber ||
          dmsScheduleAccountNumber !== currentTicket.dmsScheduleAccountNumber ||
          Number(ticketPriorityId ?? 0) !== Number(currentTicket.ticketPriorityId ?? 0) ||
          nextScheduleSubCategoryId !== currentScheduleSubCategoryId ||
          nextRequestSubCategoryId !== currentRequestSubCategoryId ||
          (isScheduleTicket && nextWorkTypeId !== currentWorkTypeId) ||
          (nextAssignTo != null && nextAssignTo !== currentAssignTo);

        const payload = {
          ticketId,
          ticketRoofTopId: currentTicket.roofTop.id || '',
          amount: normalizedAmount,
          claimStatusId,
          claimSubmittedDate: ticketData?.claimSubmittedDate
            ? format(ticketData.claimSubmittedDate, 'yyyy-MM-dd')
            : currentTicket.claimSubmittedDate,
          advisorNumber,
          techNumber,
          // TEMP: For schedule tickets, send subCategoryId; for regular tickets, send requestSubCategoryId
          subCategoryId: nextScheduleSubCategoryId,
          requestSubCategoryId: nextRequestSubCategoryId,
          dmsScheduleAccountNumber,
          ticketPriorityId,
          ...(isScheduleTicket && { workTypeId: nextWorkTypeId }),
          ...(nextAssignTo != null && {
            assignTo: nextAssignTo,
          }),
        };

        if (hasDetailsChanges) {
          ticketsMutatedCount += 1;
          mutationPromises.push(changeTicketDetailsApi(payload));
        }

        const currentText = getLastMessageDisplayText(currentTicket.lastMessage);
        if (ticketData?.text && ticketData.text !== currentText) {
          ticketsMutatedCount += 1;
          mutationPromises.push(
            createTicketMessageApi(ticketId, {
              text: ticketData.text,
              assignToOppositeUser: false,
            })
          );
        }
      });

      if (mutationPromises.length === 0) {
        setIsSavingTickets(false);
        setEditMode(false);
        setSelectedTicketsIds([]);
        return;
      }

      try {
        // Step 1: Wait for every mutation to settle.
        const results = await Promise.allSettled(mutationPromises);

        // Step 2: Report success/failure via snackbar.
        const failedCount = results.filter(r => r.status === 'rejected').length;
        if (failedCount > 0) {
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: `${failedCount} ticket update${
                failedCount === 1 ? '' : 's'
              } failed.`,
              snackBarSeverity: 'error',
            })
          );
        } else {
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: `${ticketsMutatedCount} ticket${
                ticketsMutatedCount === 1 ? '' : 's'
              } updated successfully!`,
              snackBarSeverity: 'success',
            })
          );
        }

        // Step 3: Invalidate queries — this triggers a refetch.
        // isLoadingTickets includes isSavingTickets + isRefetching,
        // so the loading animation stays visible until new data arrives.
        await Promise.all([
          queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]),
          queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]),
          queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]),
          queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]),
          queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsTotalsByRoofTop]),
        ]);
      } finally {
        // Step 4: Clear saving flag and exit edit mode after
        // fresh data has loaded (or if an error occurred).
        setIsSavingTickets(false);
        setEditMode(false);
        setSelectedTicketsIds([]);
      }
    },
    [
      dispatch,
      queryClient,
      setEditMode,
      setIsSavingTickets,
      setSelectedTicketsIds,
      ticketsPages,
      selectedTicketsIds,
      ticketPrioritiesOptions,
      ticketSubcategoriesOptions,
    ]
  );

  const handleSelectTicket = useCallback(
    (ticketId: string, payload: SelectTicketPayloadT) => (): void => {
      const tabLabel = payload.lineNumber
        ? `${payload.roNumber} - ${payload.lineNumber}`
        : payload.roNumber;
      const isFromScheduleList = activeTab === ActiveTabTypeE.Schedule;
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('section');
      searchParams.delete('tabLabel');
      if (isFromScheduleList) {
        searchParams.set('section', 'schedule');
        searchParams.set('tabLabel', tabLabel);
      }
      const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
      const path = `${RoutePathsE.TICKET_DETAIL}/${ticketId}${search}`;

      if (isFromScheduleList) {
        setOpenedScheduleTicketTab(tabLabel, ticketId);
        setActiveTab(ActiveTabTypeE.Schedule);
        sessionStorage.setItem('activeTab', ActiveTabTypeE.Schedule);
        // Preserve isScheduleTicketList - don't overwrite; ensures correct view (cards vs list) on return
      } else {
        setActiveTab(ActiveTabTypeE.Tickets);
        sessionStorage.setItem('activeTab', ActiveTabTypeE.Tickets);
        setOpenedTicketTab(tabLabel, ticketId);
      }
      saveTicketListScrollPosition();
      setTicketId(ticketId);
      navigate(path);
    },
    [
      activeTab,
      location.search,
      setOpenedTicketTab,
      setOpenedScheduleTicketTab,
      setActiveTab,
      setTicketId,
      navigate,
      saveTicketListScrollPosition,
    ]
  );

  return {
    handleSelectTicket,
    formMethods,
    handleSaveChanges,
    hasNextTicketsPage,
  };
};

export default useTicketsPage;
